'use client';

import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import NewStoreSession from './session/new-store-session';
import QuickSearch from './session/quick-search';
import SoloMealSection from './session/solo-meal-section';
import NewStoreBanner from './session/store-banner';
import NewStoreCategory from './session/store-category';
import NewStoreDeal from './session/store-deal';
import StoreQuickFilter from './session/store-quick-filter';
import SimpleStoreList from './simple-store-list';

import DesktopLayout from '@/components/desktop-layout';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { CITIES, useAiFoodOrderStore } from '@/store';

const AmoriContainer = dynamic(
  () => import('@/app/[locale]/amori/amori-container'),
  { ssr: false }
);
import { useGetCampaignBanners } from '@/api/use-get-campaign-banners';

interface CityPageClientWrapperProps {
  citySlug: string;
  // Server-provided data for initial render (SSR / hydration)
  initialData?: {
    stores: any[];
    categories?: any[]; // Categories for NewStoreCategory section
  };
}

export default function CityPageClientWrapper({
  citySlug: initialCitySlug,
  initialData,
}: CityPageClientWrapperProps) {
  const isWebinapp = useDetectWebInApp();
  const isMobile = useIsMobile();
  const params = useParams();
  const [isAmoriOpen, setIsAmoriOpen] = useState(false);

  // In webinapp mode, get citySlug from URL params for reactive updates
  // In native web mode, use initialCitySlug from server props
  const citySlug = useMemo(() => {
    if (isWebinapp && params.city) {
      return params.city as string;
    }
    return initialCitySlug;
  }, [isWebinapp, params.city, initialCitySlug]);

  // Get current address from store
  const address = useAiFoodOrderStore.use.address();

  // Find city data - will update when citySlug changes
  const cityData = useMemo(
    () => CITIES.find((c) => c.slug === citySlug),
    [citySlug]
  );

  const cityId = cityData?.value ?? '';

  // AF-062 — 1 call lấy cả 2 type (BE hỗ trợ array param), rồi tách theo type.
  // Hero ← FEATURED_BANNER; dải Super Deal ← SUPER_DEAL. Refetch khi city đổi.
  const { data: banners = [], isLoading: bannersLoading } =
    useGetCampaignBanners(
      {
        city_id: cityId,
        screen: 'home',
        type: ['FEATURED_BANNER', 'SUPER_DEAL'],
      },
      { enabled: !!cityId }
    );
  const featuredBanners = useMemo(
    () => banners.filter((b) => b.type === 'FEATURED_BANNER'),
    [banners]
  );
  const superDeals = useMemo(
    () => banners.filter((b) => b.type === 'SUPER_DEAL'),
    [banners]
  );

  const mainContent = (
    <>
      {/* Categories Section */}
      <NewStoreCategory city={citySlug} initialData={initialData?.categories} />

      {/* Quick Search Filters */}
      <QuickSearch citySlug={citySlug} />

      <NewStoreBanner banners={featuredBanners} isLoading={bannersLoading} />
      <SoloMealSection lat={address?.lat ?? 0} lng={address?.lng ?? 0} />

      <NewStoreDeal
        cityId={cityId}
        citySlug={citySlug}
        superDeals={superDeals}
        superDealsLoading={bannersLoading}
      />

      {/* New Store Section */}
      <NewStoreSession cityId={cityId} citySlug={citySlug} />

      {/* Store List Section */}
      <div className="pb-8 pt-0 w-full">
        <StoreQuickFilter citySlug={citySlug} />
        <SimpleStoreList
          data={initialData?.stores ?? []}
          payload={{ city_id: cityId }}
          processedDeals={[]}
        />
      </div>
    </>
  );

  return (
    <>
      {/* Mobile: ScrollArea with fullscreen Amori */}
      {isMobile ? (
        <>
          <ScrollArea className="mt-[calc(var(--main-layout-header-height,_0px)_*-1)] bg-white [&>div>div]:!block">
            {mainContent}
          </ScrollArea>
          <AmoriContainer isOpen={isAmoriOpen} setOpenChange={setIsAmoriOpen} />
        </>
      ) : (
        /* Desktop: full-width single canvas. The Amori chat lives in the
           global ChatBot widget (floating button + compact panel). */
        <div className="bg-white min-h-screen -mt-32 pt-28">
          <DesktopLayout className="pt-6">{mainContent}</DesktopLayout>
        </div>
      )}
    </>
  );
}
